import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, X, UserPlus, CalendarDays, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { listQuotes, listTeamMembers, createTeamMember, deleteTeamMember, bookJob, updateQuote, getQuote, listAbsences, createAbsence, deleteAbsence, deleteItem, listAppointments, createAppointment, deleteAppointment } from "@/lib/api";
import api from "@/lib/api";
import { format, startOfWeek, addDays, addWeeks, subWeeks, parse, isValid, isSameDay } from "date-fns";
import { useIsMobile } from "@/components/custom/AppShell";

const HOURS_PER_DAY = 7;

function formatDuration(hours) {
  if (!hours || hours === 0) return "";
  if (hours >= HOURS_PER_DAY) {
    const days = hours / HOURS_PER_DAY;
    return `${days % 1 === 0 ? days : days.toFixed(1)} day${days !== 1 ? "s" : ""}`;
  }
  return `${hours} hrs`;
}

// Workday defaults
const WORKDAY_START = "08:00";
const WORKDAY_END = "17:00";

// Check if a date should be skipped based on skip_weekends setting
function shouldSkipDay(date, skipWeekends) {
  if (!skipWeekends || skipWeekends === "none") return false;
  const dow = date.getDay(); // 0=Sun, 6=Sat
  if (skipWeekends === "both") return dow === 0 || dow === 6;
  if (skipWeekends === "saturday") return dow === 6;
  if (skipWeekends === "sunday") return dow === 0;
  return false;
}

// Count working days between two dates respecting skip_weekends
function countWorkingDays(startDate, endDate, skipWeekends) {
  let count = 0;
  let d = new Date(startDate);
  const end = new Date(endDate);
  while (d <= end) {
    if (!shouldSkipDay(d, skipWeekends)) count++;
    d = addDays(d, 1);
  }
  return count;
}

// Get the display time for a specific day of a multi-day job
function getJobTimeForDay(job, dayDate) {
  if (!job.start_date) return { start: job.booked_start_time || WORKDAY_START, end: job.booked_end_time || WORKDAY_END };
  const sd = parse(job.start_date, "dd/MM/yyyy", new Date());
  const ed = job.end_date ? parse(job.end_date, "dd/MM/yyyy", new Date()) : sd;
  const isMultiDay = isValid(sd) && isValid(ed) && !isSameDay(sd, ed);
  if (!isMultiDay) {
    return { start: job.booked_start_time || WORKDAY_START, end: job.booked_end_time || WORKDAY_END };
  }
  const isFirst = isSameDay(dayDate, sd);
  const isLast = isSameDay(dayDate, ed);
  if (isFirst) return { start: job.booked_start_time || WORKDAY_START, end: WORKDAY_END };
  if (isLast) return { start: WORKDAY_START, end: job.booked_end_time || WORKDAY_END };
  return { start: WORKDAY_START, end: WORKDAY_END };
}

// Generate time slots from 06:00 to 20:00 in 30-min intervals
function getTimeSlots() {
  const slots = [];
  for (let h = 6; h <= 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 20 && m > 0) break;
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
}
const TIME_SLOTS = getTimeSlots();

export default function CalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingId = searchParams.get("book");
  const skipAuth = searchParams.get("skip_auth") === "true";

  const [quotes, setQuotes] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  // Persist weekStart in URL param so back button preserves the week
  const weekParam = searchParams.get("week");
  const [weekStart, setWeekStartState] = useState(() => {
    if (weekParam) {
      const parsed = parse(weekParam, "yyyy-MM-dd", new Date());
      if (isValid(parsed)) return startOfWeek(parsed, { weekStartsOn: 1 });
    }
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  });
  const setWeekStart = (newWeek) => {
    setWeekStartState(newWeek);
    const params = new URLSearchParams(searchParams);
    params.set("week", format(newWeek, "yyyy-MM-dd"));
    params.delete("book");
    setSearchParams(params, { replace: true });
  };

  // Calendar view mode: week or month
  const [calView, setCalView] = useState(() => localStorage.getItem("cal_view") || "week");
  const [monthDate, setMonthDate] = useState(new Date());
  const toggleCalView = (v) => { setCalView(v); localStorage.setItem("cal_view", v); };
  const [memberFilter, setMemberFilterState] = useState(searchParams.get("member") || "all");
  const setMemberFilter = (val) => {
    setMemberFilterState(val);
    const params = new URLSearchParams(searchParams);
    if (val && val !== "all") params.set("member", val); else params.delete("member");
    setSearchParams(params, { replace: true });
  };

  const [bookingQuote, setBookingQuote] = useState(null);

  // Booking dialog state
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [showBookingEmailPrompt, setShowBookingEmailPrompt] = useState(null); // {quote, isReschedule}
  const [bookMemberId, setBookMemberId] = useState("");
  const [bookStartTime, setBookStartTime] = useState("08:00");
  const [bookEndTime, setBookEndTime] = useState("17:00");
  const [bookStartDate, setBookStartDate] = useState(null);
  const [bookEndDate, setBookEndDate] = useState(null);
  const [bookDateField, setBookDateField] = useState("start"); // which field the picker edits
  const [bookStartDateOpen, setBookStartDateOpen] = useState(false);
  const [bookEndDateOpen, setBookEndDateOpen] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState("");
  const [additionalMembers, setAdditionalMembers] = useState([]); // [{team_member_id, start_date, end_date, start_time, end_time}]
  const [bookSkipWeekends, setBookSkipWeekends] = useState("none");

  // Action authorization (execution bookings only)
  const [showAuthActions, setShowAuthActions] = useState(false);
  const [authActions, setAuthActions] = useState({});
  const [authQuoteData, setAuthQuoteData] = useState(null);

  // Add team member dialog
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", contact_number: "", member_type: "team_member" });
  const [showCopyJob, setShowCopyJob] = useState(null); // job to copy
  const [copyDate, setCopyDate] = useState("");
  const [copyDateOpen, setCopyDateOpen] = useState(false);
  const [editAdditionalMembers, setEditAdditionalMembers] = useState([]);
  const [editSkipWeekends, setEditSkipWeekends] = useState("none");
  const [editAccessMethod, setEditAccessMethod] = useState("");
  const [editAccessDetails, setEditAccessDetails] = useState("");

  // Job preview dialog
  const [previewJob, setPreviewJob] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editDateOpen, setEditDateOpen] = useState(false);
  const [editEndDateOpen, setEditEndDateOpen] = useState(false);
  const [editMemberId, setEditMemberId] = useState("");

  // Absence dialog
  const [showAbsence, setShowAbsence] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [absence, setAbsence] = useState({ team_member_id: "", reason: "Vacation (paid)", from_date: "", to_date: "", duration: "Full day" });
  const [absFromOpen, setAbsFromOpen] = useState(false);
  const [absToOpen, setAbsToOpen] = useState(false);
  const [previewAbsence, setPreviewAbsence] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [showAppt, setShowAppt] = useState(false);
  const [newAppt, setNewAppt] = useState({ team_member_id: "", title: "", date: "", end_date: "", start_time: "08:00", end_time: "17:00", notes: "" });
  const [apptDateOpen, setApptDateOpen] = useState(false);
  const [previewAppt, setPreviewAppt] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [q, m, a, ap] = await Promise.all([listQuotes(), listTeamMembers(), listAbsences(), listAppointments()]);
      setQuotes(q);
      setTeamMembers(m);
      setAbsences(a);
      setAppointments(ap);
      if (bookingId) {
        const found = q.find(j => j.id === bookingId);
        setBookingQuote(found || null);
        if (found) setCalView("week");
      }
    } catch {
      toast.error("Failed to load data");
    }
  }, [bookingId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  // Group booked jobs by team member + date (multi-day spanning)
  const jobGrid = useMemo(() => {
    const grid = {};
    const addToGrid = (memberId, startStr, endStr, quote) => {
      try {
        const start = parse(startStr, "dd/MM/yyyy", new Date());
        if (!isValid(start)) return;
        const end = endStr ? parse(endStr, "dd/MM/yyyy", new Date()) : start;
        const endValid = isValid(end) && end >= start ? end : start;
        const skip = quote.skip_weekends || "none";
        let d = start;
        while (d <= endValid) {
          if (!shouldSkipDay(d, skip)) {
            const key = `${memberId}_${format(d, "yyyy-MM-dd")}`;
            if (!grid[key]) grid[key] = [];
            if (!grid[key].find(j => j.id === quote.id && j._display_member === memberId && j._additional_booking_id === quote._additional_booking_id)) {
              grid[key].push({ ...quote, _display_member: memberId });
            }
          }
          d = addDays(d, 1);
        }
      }  catch (e) {}
    };
    quotes.forEach(q => {
      if (!q.start_date || !q.booked_team_member_id) return;
      // Primary team member
      addToGrid(q.booked_team_member_id, q.start_date, q.end_date, q);
      // Additional team members
      (q.additional_team_members || []).forEach(am => {
        addToGrid(am.team_member_id, am.start_date || q.start_date, am.end_date || q.end_date, q);
      });
      // Additional bookings (separate visit dates for same job)
      (q.additional_bookings || []).forEach(ab => {
        const abQuote = { ...q, _additional_booking_id: ab.id, start_date: ab.start_date, end_date: ab.end_date || ab.start_date, booked_team_member_id: ab.team_member_id, booked_team_member_name: ab.team_member_name, booked_start_time: ab.booked_start_time, booked_end_time: ab.booked_end_time, skip_weekends: ab.skip_weekends || q.skip_weekends || "none" };
        addToGrid(ab.team_member_id, ab.start_date, ab.end_date || ab.start_date, abQuote);
        (ab.additional_members || []).forEach(am => {
          addToGrid(am.team_member_id, am.start_date || ab.start_date, am.end_date || ab.end_date || ab.start_date, abQuote);
        });
      });
    });
    return grid;
  }, [quotes]);

  // Group absences by team member + date
  const absenceGrid = useMemo(() => {
    const grid = {};
    absences.forEach(a => {
      try {
        const from = parse(a.from_date, "dd/MM/yyyy", new Date());
        const to = parse(a.to_date, "dd/MM/yyyy", new Date());
        if (!isValid(from) || !isValid(to)) return;
        let d = from;
        while (d <= to) {
          const key = `${a.team_member_id}_${format(d, "yyyy-MM-dd")}`;
          if (!grid[key]) grid[key] = [];
          grid[key].push(a);
          d = addDays(d, 1);
        }
      } catch { /* skip */ }
    });
    return grid;
  }, [absences]);

  // Group appointments by team member + date
  const apptGrid = useMemo(() => {
    const grid = {};
    appointments.forEach(a => {
      try {
        const from = parse(a.date, "dd/MM/yyyy", new Date());
        const to = a.end_date ? parse(a.end_date, "dd/MM/yyyy", new Date()) : from;
        if (!isValid(from)) return;
        const endValid = isValid(to) && to >= from ? to : from;
        let d = from;
        while (d <= endValid) {
          const key = `${a.team_member_id}_${format(d, "yyyy-MM-dd")}`;
          if (!grid[key]) grid[key] = [];
          grid[key].push(a);
          d = addDays(d, 1);
        }
      }  catch (e) {}
    });
    return grid;
  }, [appointments]);

  const handleCreateAppt = async () => {
    if (!newAppt.team_member_id) { toast.error("Select a team member"); return; }
    if (!newAppt.title.trim()) { toast.error("Title required"); return; }
    if (!newAppt.date) { toast.error("Date required"); return; }
    try {
      await createAppointment(newAppt);
      toast.success("Appointment created");
      setShowAppt(false);
      setNewAppt({ team_member_id: "", title: "", date: "", end_date: "", start_time: "08:00", end_time: "17:00", notes: "" });
      fetchData();
    } catch { toast.error("Failed"); }
  };


  const handleCellClick = async (day, memberId) => {
    if (!bookingQuote) return;
    const isExecution = bookingQuote.status !== "Not Started";
    const bookedStatuses = ["Job Booked", "Recall Booked", "Quote Booked", "Job Signed", "Invoiced"];
    const isAdditionalBooking = bookingQuote.booked_team_member_id && bookingQuote.start_date && bookedStatuses.includes(bookingQuote.status);

    if (isExecution && !skipAuth && !isAdditionalBooking) {
      // Load full quote data for action checklist
      try {
        const full = await getQuote(bookingQuote.id);
        const mainActions = (full.actions || []).filter(a => typeof a === "object" && !a.is_sub);
        if (mainActions.length > 0) {
          setAuthQuoteData(full);
          const checked = {};
          mainActions.forEach((_, idx) => { checked[idx] = true; });
          setAuthActions(checked);
          setBookStartDate(day);
          setBookEndDate(day);
          setBookMemberId(memberId);
          setShowAuthActions(true);
          return;
        }
      } catch { /* fall through */ }
    }

    setBookStartDate(day);
    setBookEndDate(day);
    setBookMemberId(memberId);
    setBookStartTime("08:00");
    setBookEndTime("17:00");
    setShowBookDialog(true);
  };

  const handleConfirmAuth = async () => {
    if (!authQuoteData) return;
    const selectedCount = Object.values(authActions).filter(Boolean).length;
    if (selectedCount === 0) { toast.error("Select at least one action"); return; }

    const allActions = authQuoteData.actions || [];
    const totalMainActions = allActions.filter(a => typeof a === "object" && !a.is_sub).length;
    const hasDeselected = selectedCount < totalMainActions;

    // If some actions are deselected, create a duplicate with ALL original actions first
    if (hasDeselected) {
      try {
        await api.post(`/quotes/${authQuoteData.id}/duplicate`);
        toast.info("Full quote saved as a duplicate for future reference");
      }  catch (e) {}
    }

    // Filter actions: keep selected main actions + their sub-actions
    const newActions = [];
    const removedArrayIndices = [];
    let mainIdx = 0;
    for (let i = 0; i < allActions.length; i++) {
      const act = allActions[i];
      if (!act.is_sub) {
        if (authActions[mainIdx]) {
          newActions.push(act);
          let j = i + 1;
          while (j < allActions.length && allActions[j].is_sub) {
            newActions.push(allActions[j]);
            j++;
          }
        } else {
          removedArrayIndices.push(i);
          let j = i + 1;
          while (j < allActions.length && allActions[j].is_sub) {
            removedArrayIndices.push(j);
            j++;
          }
        }
        mainIdx++;
      }
    }

    // Update the quote with only authorized actions
    try {
      await updateQuote(authQuoteData.id, { actions: newActions });

      // Delete materials linked to removed actions (by array index)
      if (removedArrayIndices.length > 0) {
        const linkedItems = (authQuoteData.items || []).filter(it => removedArrayIndices.includes(it.action_index));
        for (const it of linkedItems) {
          try { await deleteItem(authQuoteData.id, it.id); } catch { /* continue */ }
        }
      }
    } catch {
      toast.error("Failed to update actions");
      return;
    }

    setShowAuthActions(false);
    setBookStartTime("08:00");
    setBookEndTime("17:00");
    setShowBookDialog(true);
  };

  // Check for overlapping bookings
  const checkOverlap = useCallback((memberId, date, startTime, endTime) => {
    if (!memberId || !date || !startTime || !endTime) { setOverlapWarning(""); return; }
    const dateKey = format(date, "yyyy-MM-dd");
    const cellKey = `${memberId}_${dateKey}`;
    const existing = jobGrid[cellKey] || [];
    const bookingQuoteId = bookingQuote?.id;
    for (const job of existing) {
      if (job.id === bookingQuoteId) continue;
      const jStart = job.booked_start_time || "";
      const jEnd = job.booked_end_time || "";
      if (!jStart || !jEnd) continue;
      if (startTime < jEnd && endTime > jStart) {
        setOverlapWarning(`Overlaps with ${job.site_address?.split(",")[0] || "a job"} (${jStart}–${jEnd})`);
        return;
      }
    }
    setOverlapWarning("");
  }, [jobGrid, bookingQuote]);

  // Re-check overlap when time/member/date changes
  useEffect(() => {
    checkOverlap(bookMemberId, bookStartDate, bookStartTime, bookEndTime);
  }, [bookMemberId, bookStartDate, bookStartTime, bookEndTime, checkOverlap]);

  const handleConfirmBook = async () => {
    if (!bookMemberId) { toast.error("Select a team member"); return; }
    if (!bookStartDate) return;
    if (bookStartTime >= bookEndTime) { toast.error("End time must be after start time"); return; }
    if (overlapWarning) { toast.error("Cannot book — time slot overlaps with existing booking"); return; }
    const isExecution = bookingQuote.status !== "Not Started";
    const bookedStatuses = ["Job Booked", "Recall Booked", "Quote Booked", "Job Signed", "Invoiced"];
    const isAdditionalBooking = bookingQuote.booked_team_member_id && bookingQuote.start_date && bookedStatuses.includes(bookingQuote.status);
    try {
      if (isAdditionalBooking) {
        // This is an additional booking for an already-booked job
        await api.post(`/quotes/${bookingQuote.id}/add-booking`, {
          start_date: format(bookStartDate, "dd/MM/yyyy"),
          end_date: bookEndDate ? format(bookEndDate, "dd/MM/yyyy") : format(bookStartDate, "dd/MM/yyyy"),
          team_member_id: bookMemberId,
          booked_start_time: bookStartTime,
          booked_end_time: bookEndTime,
          skip_weekends: bookSkipWeekends,
          additional_members: additionalMembers.map(am => ({
            team_member_id: am.team_member_id,
            start_date: am.start_date || format(bookStartDate, "dd/MM/yyyy"),
            end_date: am.end_date || (bookEndDate ? format(bookEndDate, "dd/MM/yyyy") : format(bookStartDate, "dd/MM/yyyy")),
            start_time: am.start_time || bookStartTime,
            end_time: am.end_time || bookEndTime,
          })),
        });
      } else {
        await bookJob(bookingQuote.id, {
          start_date: format(bookStartDate, "dd/MM/yyyy"),
          end_date: bookEndDate ? format(bookEndDate, "dd/MM/yyyy") : format(bookStartDate, "dd/MM/yyyy"),
          team_member_id: bookMemberId,
          booked_start_time: bookStartTime,
          booked_end_time: bookEndTime,
          skip_weekends: bookSkipWeekends,
          additional_members: additionalMembers.map(am => ({
            team_member_id: am.team_member_id,
            start_date: am.start_date || format(bookStartDate, "dd/MM/yyyy"),
            end_date: am.end_date || (bookEndDate ? format(bookEndDate, "dd/MM/yyyy") : format(bookStartDate, "dd/MM/yyyy")),
            start_time: am.start_time || bookStartTime,
            end_time: am.end_time || bookEndTime,
          })),
        });
      }
      setShowBookDialog(false);
      setAdditionalMembers([]);
      setBookSkipWeekends("none");
      toast.success(isAdditionalBooking ? "Additional booking added" : isExecution ? "Execution booked — status updated to Job Booked" : "Quote appointment booked — status updated to Quote Booked");
      // Save email data before clearing booking state
      const emailData = bookingQuote.correspondence_email ? {
        quote: bookingQuote,
        date: format(bookStartDate, "dd/MM/yyyy"),
        time: bookStartTime,
        isReschedule: false,
      } : null;
      // Exit booking mode immediately
      setBookingQuote(null);
      setSearchParams({}, { replace: true });
      // Show email prompt after exiting booking mode
      if (emailData) {
        setShowBookingEmailPrompt(emailData);
      }
      // Don't call fetchData() here — the useEffect will re-fetch after bookingId clears
      // Manually refresh quotes/team data without re-setting bookingQuote
      const [q, m, a, ap] = await Promise.all([listQuotes(), listTeamMembers(), listAbsences(), listAppointments()]);
      setQuotes(q);
      setTeamMembers(m);
      setAbsences(a);
      setAppointments(ap);
    } catch {
      toast.error("Failed to book");
    }
  };

  const handleAddMember = async () => {
    if (!newMember.name.trim()) { toast.error("Name is required"); return; }
    try {
      await createTeamMember(newMember);
      toast.success("Team member added");
      setShowAddMember(false);
      setNewMember({ name: "", contact_number: "", member_type: "" });
      fetchData();
    } catch {
      toast.error("Failed to add team member");
    }
  };

  const handleJobCardClick = async (job) => {
    if (bookingQuote) return;
    setPreviewLoading(true);
    try {
      const full = await getQuote(job.id);
      setPreviewJob(full);
      setEditDate(full.start_date || "");
      setEditEndDate(full.end_date || full.start_date || "");
      setEditStartTime(full.booked_start_time || "08:00");
      setEditEndTime(full.booked_end_time || "17:00");
      setEditMemberId(full.booked_team_member_id || "");
      setEditAdditionalMembers(full.additional_team_members || []);
      setEditSkipWeekends(full.skip_weekends || "none");
      setEditAccessMethod(full.access_method || "");
      setEditAccessDetails(full.access_branch_address || full.access_occupier_phone || full.access_notes || "");
    } catch {
      toast.error("Failed to load job details");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!previewJob) return;
    if (!editDate) { toast.error("Select a date"); return; }
    if (editStartTime >= editEndTime) { toast.error("End time must be after start time"); return; }
    try {
      const member = teamMembers.find(m => m.id === editMemberId);
      const memberChanged = editMemberId && editMemberId !== previewJob.booked_team_member_id;
      const updates = {
        start_date: editDate,
        end_date: editEndDate || editDate,
        booked_start_time: editStartTime,
        booked_end_time: editEndTime,
        skip_weekends: editSkipWeekends,
      };
      if (memberChanged) {
        updates.booked_team_member_id = editMemberId;
        updates.booked_team_member_name = member?.name || "";
      }
      updates.additional_team_members = editAdditionalMembers.map(am => ({
        team_member_id: am.team_member_id,
        name: teamMembers.find(m => m.id === am.team_member_id)?.name || am.name || "",
        start_date: am.start_date || editDate,
        end_date: am.end_date || editEndDate || editDate,
        start_time: am.start_time || editStartTime,
        end_time: am.end_time || editEndTime,
      }));
      // Access info
      updates.access_method = editAccessMethod || "";
      if (editAccessMethod === "keys_branch") updates.access_branch_address = editAccessDetails;
      else if (editAccessMethod === "occupier") updates.access_occupier_phone = editAccessDetails || previewJob?.tenant_phone || "";
      else if (editAccessMethod === "other") updates.access_notes = editAccessDetails;
      await updateQuote(previewJob.id, updates);
      toast.success(memberChanged ? `Reassigned to ${member?.name}` : "Booking updated");
      // Offer to send reschedule email
      if (previewJob.correspondence_email) {
        setShowBookingEmailPrompt({
          quote: previewJob,
          date: editDate,
          time: editStartTime,
          isReschedule: true,
        });
      }
      setPreviewJob(null);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDeleteBooking = async () => {
    if (!previewJob) return;
    try {
      // Check if this is an additional booking (not the primary)
      if (previewJob._additional_booking_id) {
        await api.delete(`/quotes/${previewJob.id}/bookings/${previewJob._additional_booking_id}`);
        toast.success("Additional booking deleted");
      } else {
        const newStatus = previewJob.booking_type === "quote" ? "Not Started" : "Quote Accepted";
        await updateQuote(previewJob.id, {
          status: newStatus,
          booked_team_member_id: "",
          booked_team_member_name: "",
          booked_start_time: "",
          booked_end_time: "",
          booking_type: "",
          start_date: "",
          end_date: "",
        });
        toast.success("Booking deleted");
      }
      setPreviewJob(null);
      fetchData();
    } catch {
      toast.error("Failed to delete booking");
    }
  };

  const handleCreateAbsence = async () => {
    if (!absence.team_member_id) { toast.error("Select a team member"); return; }
    if (!absence.from_date || !absence.to_date) { toast.error("Select dates"); return; }
    try {
      await createAbsence(absence);
      toast.success("Absence created");
      setShowAbsence(false);
      setAbsence({ team_member_id: "", reason: "Vacation (paid)", from_date: "", to_date: "", duration: "Full day" });
      fetchData();
    } catch {
      toast.error("Failed to create absence");
    }
  };

  const today = new Date();
  const isMobile = useIsMobile();

  const filteredMembers = memberFilter === "all" ? teamMembers : teamMembers.filter(m => m.id === memberFilter);

  // England bank holidays
  const BANK_HOLIDAYS = {
    "2026-01-01": "New Year's Day", "2026-04-03": "Good Friday", "2026-04-06": "Easter Monday",
    "2026-05-04": "Early May BH", "2026-05-25": "Spring BH", "2026-08-31": "Summer BH",
    "2026-12-25": "Christmas Day", "2026-12-28": "Boxing Day",
    "2027-01-01": "New Year's Day", "2027-03-26": "Good Friday", "2027-03-29": "Easter Monday",
    "2027-05-03": "Early May BH", "2027-05-31": "Spring BH", "2027-08-30": "Summer BH",
    "2027-12-27": "Christmas Day", "2027-12-28": "Boxing Day",
    "2028-01-03": "New Year's Day", "2028-04-14": "Good Friday", "2028-04-17": "Easter Monday",
    "2028-05-01": "Early May BH", "2028-05-29": "Spring BH", "2028-08-28": "Summer BH",
    "2028-12-25": "Christmas Day", "2028-12-26": "Boxing Day",
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Page Header */}
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <div className={`flex items-center justify-between ${isMobile ? "mb-3" : "mb-4"}`}>
          <h1 className={isMobile ? "text-lg font-semibold" : "ui-page-title"}>{format(weekStart, "MMMM yyyy")}</h1>
          <div className="flex items-center gap-2">
            {!isMobile && (
              <Button onClick={() => { setNewAppt({ team_member_id: "", title: "", date: format(new Date(), "dd/MM/yyyy"), end_date: "", start_time: "08:00", end_time: "17:00", notes: "" }); setShowAppt(true); }} className="btn-secondary gap-1 h-9 px-4 text-sm" data-testid="add-appt-btn">
                <CalendarDays className="w-4 h-4" /> Add Appointment
              </Button>
            )}
            {!isMobile && (
              <Button onClick={() => { setAbsence({ team_member_id: "", reason: "Vacation (paid)", from_date: format(new Date(), "dd/MM/yyyy"), to_date: format(new Date(), "dd/MM/yyyy"), duration: "Full day" }); setShowAbsence(true); }} className="btn-secondary gap-1 h-9 px-4 text-sm" data-testid="add-absence-btn">
                <CalendarDays className="w-4 h-4" /> Add Absence
              </Button>
            )}
            {!isMobile && (
              <Button onClick={() => setShowAddMember(true)} className="btn-primary gap-1 h-9 px-4 text-sm" data-testid="add-member-btn">
                <UserPlus className="w-4 h-4" /> Add Team Member
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation bar */}
      <div className={`${isMobile ? "px-4" : "px-8"} pb-4 flex items-center gap-3`}>
        {calView === "week" ? (
          <>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(subWeeks(weekStart, 1))} className="btn-secondary h-8 w-8 p-0" data-testid="prev-week">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className={`text-sm font-medium ${isMobile ? "flex-1 text-center" : "min-w-[200px] text-center"}`} style={{ color: "var(--text-primary)" }} data-testid="week-range">
              {format(weekStart, "dd/MM")} — {format(addDays(weekStart, 6), "dd/MM/yyyy")}
            </span>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, 1))} className="btn-secondary h-8 w-8 p-0" data-testid="next-week">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} className="btn-secondary h-8 w-8 p-0">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[200px] text-center" style={{ color: "var(--text-primary)" }}>
              {format(monthDate, "MMMM yyyy")}
            </span>
            <Button variant="outline" size="sm" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} className="btn-secondary h-8 w-8 p-0">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        )}
        {/* Team member filter */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide" style={{ maxWidth: "50%" }}>
          <button onClick={() => setMemberFilter("all")} className="flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors" style={memberFilter === "all" ? { background: "#E05C2A", color: "#fff" } : { background: "var(--surface)", color: "var(--text-secondary)", border: "0.5px solid var(--border)" }}>All</button>
          {teamMembers.map(m => (
            <button key={m.id} onClick={() => setMemberFilter(m.id)} className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors" style={memberFilter === m.id ? { background: "#E05C2A", color: "#fff" } : { background: "var(--surface)", color: "var(--text-secondary)", border: "0.5px solid var(--border)" }}>
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: m.color || "#64748b" }} />
              {m.name.split(" ")[0]}
            </button>
          ))}
        </div>
        {/* View toggle */}
        <div className="ml-auto flex rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <button onClick={() => toggleCalView("week")} className="px-3 py-1.5 text-xs font-semibold transition-colors" style={calView === "week" ? { background: "#E05C2A", color: "#fff" } : { background: "var(--surface)", color: "var(--text-secondary)" }}>Week</button>
          <button onClick={() => toggleCalView("month")} className="px-3 py-1.5 text-xs font-semibold transition-colors" style={calView === "month" ? { background: "#E05C2A", color: "#fff" } : { background: "var(--surface)", color: "var(--text-secondary)" }}>Month</button>
        </div>
      </div>
      {/* Filter indicator */}
      {memberFilter !== "all" && (() => {
        const m = teamMembers.find(t => t.id === memberFilter);
        return m ? <div className={isMobile ? "px-4 pb-1" : "px-8 pb-1"}><p className="text-[11px]" style={{ color: "var(--text-muted)" }}>Showing: <span className="font-medium" style={{ color: "var(--text-secondary)" }}>{m.name}</span></p></div> : null;
      })()}

      {/* Booking banner */}
      {bookingQuote && (
        <div className="mx-6 mt-3 p-3 bg-orange-50 border border-orange-200 rounded-sm flex items-center justify-between" data-testid="booking-banner">
          <div className="text-sm">
            <span className="font-semibold text-orange-700">Booking: </span>
            <span className="font-medium text-slate-800">{bookingQuote.site_address || "No address"}</span>
            <span className="text-slate-500 ml-2">({bookingQuote.client_type})</span>
            <span className="text-slate-500 ml-2">Duration: {formatDuration(bookingQuote.labour_hours)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-orange-600">Click a day to book</span>
            <button onClick={() => { setBookingQuote(null); navigate("/calendar", { replace: true }); }} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Weekly Calendar Grid */}
      {calView === "week" && (
      <div className={isMobile ? "px-2 py-2 overflow-x-auto" : "px-8 py-3"}>
        <div className="ui-surface overflow-hidden" style={isMobile ? { minWidth: "700px" } : { tableLayout: "fixed" }}>
          {/* Day headers */}
          <div className="grid" style={{ gridTemplateColumns: isMobile ? "80px repeat(7, 1fr)" : "120px repeat(7, 1fr)" }}>
            <div className="bg-slate-100 border-b border-r border-slate-200 p-2" />
            {weekDays.map(day => {
              const isToday = isSameDay(day, today);
              const bh = BANK_HOLIDAYS[format(day, "yyyy-MM-dd")];
              return (
                <div
                  key={day.toISOString()}
                  className={`border-b border-r border-slate-200 p-2 text-center ${
                    bh ? "bg-red-50" : isToday ? "bg-blue-50" : "bg-slate-50"
                  }`}
                  data-testid={`day-header-${format(day, "yyyy-MM-dd")}`}
                >
                  <div className={`text-xs font-semibold uppercase ${bh ? "text-red-500" : isToday ? "text-blue-600" : "text-slate-500"}`}>
                    {format(day, "EEE")}
                  </div>
                  <div className={`text-lg font-bold ${bh ? "text-red-600" : isToday ? "text-white" : "text-slate-800"}`}>
                    {isToday ? (
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full" style={{ background: "#EF4444" }}>{format(day, "d")}</span>
                    ) : format(day, "d")}
                  </div>
                  {bh && <div className="text-[9px] font-bold text-red-500 leading-tight mt-0.5">{bh}</div>}
                </div>
              );
            })}
          </div>

          {/* Team member rows */}
          {filteredMembers.map(member => {
            // Compute spanning bars for multi-day jobs for this member this week
            // Include additional bookings as separate virtual entries
            const memberJobs = [];
            quotes.forEach(q => {
              if (!q.start_date) return;
              // Lead contractor — use the job's dates
              if (q.booked_team_member_id === member.id) {
                memberJobs.push(q);
              }
              // Additional team members — use THEIR individual dates
              (q.additional_team_members || []).forEach(am => {
                if (am.team_member_id === member.id) {
                  memberJobs.push({ ...q, _am_member: true, start_date: am.start_date || q.start_date, end_date: am.end_date || q.end_date, booked_start_time: am.start_time || q.booked_start_time, booked_end_time: am.end_time || q.booked_end_time });
                }
              });
              // Additional bookings for this member
              (q.additional_bookings || []).forEach(ab => {
                if (ab.team_member_id === member.id) {
                  memberJobs.push({ ...q, _additional_booking_id: ab.id, start_date: ab.start_date, end_date: ab.end_date || ab.start_date, booked_team_member_id: ab.team_member_id, booked_team_member_name: ab.team_member_name, booked_start_time: ab.booked_start_time, booked_end_time: ab.booked_end_time, skip_weekends: ab.skip_weekends || q.skip_weekends || "none" });
                }
                (ab.additional_members || []).forEach(am => {
                  if (am.team_member_id === member.id) {
                    memberJobs.push({ ...q, _additional_booking_id: ab.id, _am_member: true, start_date: am.start_date || ab.start_date, end_date: am.end_date || ab.end_date || ab.start_date, booked_start_time: am.start_time || ab.booked_start_time, booked_end_time: am.end_time || ab.booked_end_time, skip_weekends: ab.skip_weekends || q.skip_weekends || "none" });
                  }
                });
              });
            });
            const multiDayBars = [];
            const singleDayByDate = {};
            const renderedMultiIds = new Set();

            memberJobs.forEach(q => {
              try {
                const sd = parse(q.start_date, "dd/MM/yyyy", new Date());
                const ed = q.end_date ? parse(q.end_date, "dd/MM/yyyy", new Date()) : sd;
                if (!isValid(sd)) return;
                const endValid = isValid(ed) && ed >= sd ? ed : sd;
                const skip = q.skip_weekends || "none";
                const isMulti = !isSameDay(sd, endValid);

                if (isMulti) {
                  // Find which columns this job spans in the current week, split around skipped days
                  const weekEnd = addDays(weekStart, 6);
                  const visStart = sd < weekStart ? weekStart : sd;
                  const visEnd = endValid > weekEnd ? weekEnd : endValid;
                  if (visStart > weekEnd || visEnd < weekStart) return;

                  // Build segments: contiguous runs of non-skipped days
                  let segStart = null;
                  let d = visStart;
                  while (d <= visEnd) {
                    const col = Math.round((d - weekStart) / 86400000);
                    if (shouldSkipDay(d, skip)) {
                      // End current segment if any
                      if (segStart !== null) {
                        const segStartCol = Math.round((segStart - weekStart) / 86400000);
                        const segEndCol = Math.round((addDays(d, -1) - weekStart) / 86400000);
                        const span = segEndCol - segStartCol + 1;
                        const isFirstDay = isSameDay(segStart, sd);
                        const isLastDay = isSameDay(addDays(d, -1), endValid);
                        const continuesLeft = sd < weekStart && isSameDay(segStart, weekStart);
                        const continuesRight = endValid > weekEnd && isSameDay(addDays(d, -1), weekEnd);
                        multiDayBars.push({ job: q, startCol: segStartCol, span, isFirstDay, isLastDay, continuesLeft, continuesRight });
                        segStart = null;
                      }
                    } else {
                      if (segStart === null) segStart = d;
                    }
                    d = addDays(d, 1);
                  }
                  // Close final segment
                  if (segStart !== null) {
                    const segStartCol = Math.round((segStart - weekStart) / 86400000);
                    const segEndCol = Math.round((visEnd - weekStart) / 86400000);
                    const span = segEndCol - segStartCol + 1;
                    const isFirstDay = isSameDay(segStart, sd);
                    const isLastDay = isSameDay(visEnd, endValid);
                    const continuesLeft = sd < weekStart && isSameDay(segStart, weekStart);
                    const continuesRight = endValid > weekEnd && isSameDay(visEnd, weekEnd);
                    multiDayBars.push({ job: q, startCol: segStartCol, span, isFirstDay, isLastDay, continuesLeft, continuesRight });
                  }
                  renderedMultiIds.add(q._additional_booking_id ? `${q.id}_${q._additional_booking_id}` : q.id);
                } else {
                  // Single day — check if it's skipped
                  if (!shouldSkipDay(sd, skip)) {
                    const dateKey = format(sd, "yyyy-MM-dd");
                    if (!singleDayByDate[dateKey]) singleDayByDate[dateKey] = [];
                    singleDayByDate[dateKey].push(q);
                  }
                }
              }  catch (e) {}
            });

            // Sort multi-day bars by start time
            multiDayBars.sort((a, b) => (a.job.booked_start_time || "99:99").localeCompare(b.job.booked_start_time || "99:99"));

            return (
            <div key={member.id} className="relative">
              <div className="grid" style={{ gridTemplateColumns: isMobile ? "80px repeat(7, 1fr)" : "120px repeat(7, 1fr)" }}>
              {/* Member info */}
              <div className="border-b border-r border-slate-200 p-1.5 flex items-center gap-2 group" title={`${member.name}\n${member.contact_number}`} style={multiDayBars.length > 0 ? { paddingTop: `${multiDayBars.length * 52 + 6}px` } : {}}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                  style={{ backgroundColor: member.color || "#64748b" }}
                  data-testid={`member-${member.initials}`}
                >
                  {member.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>{member.name.split(" ")[0]}</p>
                  <p className="text-[8px] truncate" style={{ color: "var(--text-muted)" }}>{member.member_type === "sub_contractor" ? "Sub Contractor" : member.name.split(" ").slice(1).join(" ")}</p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Remove ${member.name} from the team?`)) return;
                    try { await deleteTeamMember(member.id); toast.success("Team member removed"); fetchData(); } catch { toast.error("Failed to remove"); }
                  }}
                  className="text-[9px] text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  title="Remove team member"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>

              {/* Day cells (single-day jobs + absences only) */}
              {weekDays.map(day => {
                const dateKey = format(day, "yyyy-MM-dd");
                const cellKey = `${member.id}_${dateKey}`;
                const singleJobs = (singleDayByDate[dateKey] || []).sort((a, b) => (a.booked_start_time || "99:99").localeCompare(b.booked_start_time || "99:99"));
                const cellAbsences = absenceGrid[cellKey] || [];
                const cellAppts = apptGrid[cellKey] || [];
                const isToday = isSameDay(day, today);

                return (
                  <div
                    key={dateKey}
                    onClick={() => bookingQuote && handleCellClick(day, member.id)}
                    className={`calendar-cell border-b border-r border-slate-200 p-1 min-h-[80px] overflow-hidden ${
                      isToday ? "bg-blue-50/30" : ""
                    } ${bookingQuote ? "cursor-pointer hover:bg-orange-50/50" : ""}`}
                    style={{ ...( multiDayBars.length > 0 ? { paddingTop: `${multiDayBars.length * 52 + 6}px` } : {}), minWidth: 0 }}
                    data-testid={`cell-${member.initials}-${dateKey}`}
                  >
                    {cellAbsences.map(a => (
                      <div
                        key={a.id}
                        onClick={(e) => { e.stopPropagation(); setPreviewAbsence(a); }}
                        className="mb-1 p-1.5 rounded text-[10px] leading-tight bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100"
                        title={`${a.reason} - ${a.duration}`}
                      >
                        <div className="font-semibold text-amber-700 truncate">{a.reason}</div>
                        <div className="text-amber-500">{a.duration}</div>
                      </div>
                    ))}
                    {/* Merge appointments + jobs, sort by time */}
                    {[
                      ...cellAppts.map(ap => ({ type: "appt", time: ap.start_time || "00:00", data: ap })),
                      ...singleJobs.map(j => ({ type: "job", time: j.booked_start_time || "99:99", data: j })),
                    ].sort((a, b) => a.time.localeCompare(b.time)).map(entry => {
                      if (entry.type === "appt") {
                        const ap = entry.data;
                        return (
                          <div key={`appt-${ap.id}`} onClick={(e) => { e.stopPropagation(); setPreviewAppt(ap); }} className="mb-1 p-1.5 rounded text-[11px] leading-tight cursor-pointer hover:opacity-80 overflow-hidden" style={{ borderLeftColor: "#7C3AED", borderLeftWidth: "3px", backgroundColor: "rgba(124,58,237,0.08)" }} title={`${ap.title} ${ap.start_time}-${ap.end_time}`}>
                            <div className="text-[9px] font-bold uppercase text-purple-600 mb-0.5">Appointment</div>
                            <div className="font-semibold text-slate-800 truncate">{ap.title}</div>
                            <div className="text-slate-500 mt-0.5">{ap.start_time} - {ap.end_time}</div>
                          </div>
                        );
                      }
                      const j = entry.data;
                      return (
                        <div key={`job-${j.id}-${j._additional_booking_id || ""}`} onClick={(e) => { if (!bookingQuote) { e.stopPropagation(); handleJobCardClick(j); } }} className={`mb-1 p-1.5 rounded text-[11px] leading-tight ${!bookingQuote ? "cursor-pointer hover:opacity-80" : ""}`} style={{ borderLeftColor: member.color || "#64748b", borderLeftWidth: "3px", backgroundColor: `${member.color}12` }} title={`${j.site_address} - ${j.booked_start_time || "08:00"}-${j.booked_end_time || "17:00"}`}>
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              {j.booking_type === "quote" && <div className="text-[9px] font-bold uppercase text-sky-500 mb-0.5">Quote Appt</div>}
                              {j.booking_type === "recall" && <div className="text-[9px] font-bold uppercase text-orange-500 mb-0.5">Recall</div>}
                              {j.booking_type === "job" && <div className="text-[9px] font-bold uppercase text-emerald-600 mb-0.5">Execution</div>}
                              {j.works_type === "Works Order" && <span className="text-[8px] font-bold px-1 rounded" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>WO</span>}
                              <div className="font-semibold text-slate-800 truncate">{j.site_address?.split(",")[0] || "Job"}</div>
                              <div className="text-slate-500 mt-0.5">{j.booked_start_time || "08:00"} - {j.booked_end_time || "17:00"}</div>
                            </div>
                            {j.booking_type !== "quote" && (
                              <div className="flex-shrink-0 mt-0.5">
                                {(j.materials_status === "Ordered" || j.materials_status === "Not Applicable") ? (
                                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#D1FAE5", color: "#065F46" }}>Ordered</span>
                                ) : (
                                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#991B1B" }}>Not Started</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              </div>

              {/* Multi-day spanning bars (absolute overlay, stacked) */}
              {multiDayBars.map((bar, barIdx) => {
                const { job: j, startCol, span, isFirstDay, isLastDay, continuesLeft, continuesRight } = bar;
                const tStart = getJobTimeForDay(j, weekDays[startCol]);
                const tEnd = getJobTimeForDay(j, weekDays[startCol + span - 1]);
                const topOffset = barIdx * 52 + 4;
                return (
                  <div
                    key={`bar-${j.id}-${j._additional_booking_id || ""}-${startCol}`}
                    onClick={(e) => { e.stopPropagation(); if (!bookingQuote) handleJobCardClick(j); }}
                    className="absolute calendar-bar-layer flex flex-col justify-center px-2 py-1.5 text-[11px] leading-tight cursor-pointer hover:opacity-80 transition-opacity overflow-hidden rounded"
                    style={{
                      left: `calc(${isMobile ? "80px" : "120px"} + ${startCol} * ((100% - ${isMobile ? "80px" : "120px"}) / 7))`,
                      width: `calc(${span} * ((100% - ${isMobile ? "80px" : "120px"}) / 7))`,
                      top: `${topOffset}px`,
                      minHeight: "48px",
                      backgroundColor: `${member.color}12`,
                      borderLeftWidth: "3px",
                      borderLeftColor: member.color || "#64748b",
                      borderLeftStyle: "solid",
                    }}
                    title={`${j.site_address} (${j.start_date} → ${j.end_date})`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        {j.booking_type === "quote" && <div className="text-[9px] font-bold uppercase text-sky-500 mb-0.5">Quote Appt</div>}
                        {j.booking_type === "recall" && <div className="text-[9px] font-bold uppercase text-orange-500 mb-0.5">Recall</div>}
                        {j.booking_type === "job" && <div className="text-[9px] font-bold uppercase text-emerald-600 mb-0.5">Execution</div>}
                        <div className="font-semibold text-slate-800 truncate">{j.site_address?.split(",")[0] || "Job"}</div>
                        <div className="text-slate-500 mt-0.5">{isFirstDay ? tStart.start : "08:00"} - {isLastDay ? tEnd.end : "17:00"}</div>
                      </div>
                      {j.booking_type !== "quote" && (
                        <div className="flex-shrink-0 mt-0.5">
                          {(j.materials_status === "Ordered" || j.materials_status === "Not Applicable") ? (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#D1FAE5", color: "#065F46" }}>Ordered</span>
                          ) : (
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEE2E2", color: "#991B1B" }}>Not Started</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
          })}
        </div>
      </div>
      )}

      {/* Monthly Calendar Grid — hidden during booking */}
      {calView === "month" && !bookingQuote && (
        <MonthlyGrid
          monthDate={monthDate}
          quotes={quotes}
          appointments={appointments}
          teamMembers={teamMembers}
          memberFilter={memberFilter}
          onEventClick={(job) => handleJobCardClick(job)}
          onApptClick={(appt) => setPreviewAppt(appt)}
          onDayClick={(day) => { setNewAppt({ team_member_id: "", title: "", date: format(day, "dd/MM/yyyy"), end_date: format(day, "dd/MM/yyyy"), start_time: "08:00", end_time: "17:00", notes: "" }); setShowAppt(true); }}
        />
      )}

      {/* Action Authorization Dialog (execution bookings) */}
      <Dialog open={showAuthActions} onOpenChange={setShowAuthActions}>
        <DialogContent data-testid="auth-actions-dialog">
          <DialogHeader>
            <DialogTitle>Authorize Actions</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Select which actions are authorized for this job execution. Unselected actions will be removed from this quote but a full copy with all actions will be saved automatically.
          </p>
          {authQuoteData && (
            <div className="space-y-1 py-2 max-h-[50vh] overflow-y-auto">
              {(() => {
                const mainActions = (authQuoteData.actions || []).filter(a => typeof a === "object" && !a.is_sub);
                let mainNum = 0;
                return (authQuoteData.actions || []).map((act, i) => {
                  if (typeof act !== "object") return null;
                  if (act.is_sub) {
                    const parentIdx = mainNum - 1;
                    if (!authActions[parentIdx]) return null;
                    return (
                      <div key={i} className="pl-10 py-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
                        {act.description || "—"}
                      </div>
                    );
                  }
                  const idx = mainNum;
                  mainNum++;
                  const checked = !!authActions[idx];
                  return (
                    <label key={i} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${checked ? "bg-blue-50" : "hover:bg-gray-50"}`} style={{ border: `1px solid ${checked ? "var(--primary)" : "var(--border)"}` }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setAuthActions(p => ({ ...p, [idx]: !p[idx] }))}
                        className="mt-0.5 w-4 h-4 rounded accent-blue-600"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono font-bold" style={{ color: "var(--text-secondary)" }}>1.{idx + 1}</span>
                          <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{act.title || "—"}</span>
                        </div>
                        {act.description && <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{act.description}</p>}
                        {act.price > 0 && <p className="text-xs font-mono mt-1" style={{ color: "var(--primary)" }}>£{act.price.toFixed(2)}</p>}
                      </div>
                    </label>
                  );
                });
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuthActions(false)}>Cancel</Button>
            <Button onClick={handleConfirmAuth} className="btn-primary gap-1" data-testid="confirm-auth-btn">
              Continue to Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book Panel — draggable, no overlay */}
      {showBookDialog && (
        <DraggableBookPanel
          bookingQuote={bookingQuote}
          bookStartDate={bookStartDate}
          bookEndDate={bookEndDate}
          bookStartTime={bookStartTime}
          bookEndTime={bookEndTime}
          bookMemberId={bookMemberId}
          bookStartDateOpen={bookStartDateOpen}
          bookEndDateOpen={bookEndDateOpen}
          setBookStartDate={setBookStartDate}
          setBookEndDate={setBookEndDate}
          setBookStartTime={setBookStartTime}
          setBookEndTime={setBookEndTime}
          setBookMemberId={setBookMemberId}
          setBookStartDateOpen={setBookStartDateOpen}
          setBookEndDateOpen={setBookEndDateOpen}
          teamMembers={teamMembers}
          additionalMembers={additionalMembers}
          setAdditionalMembers={setAdditionalMembers}
          overlapWarning={overlapWarning}
          onConfirm={handleConfirmBook}
          onCancel={() => setShowBookDialog(false)}
          isSameDay={isSameDay}
          skipWeekends={bookSkipWeekends}
          setSkipWeekends={setBookSkipWeekends}
        />
      )}


      {/* Add Team Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type *</Label>
              <Select value={newMember.member_type} onValueChange={(v) => setNewMember(p => ({ ...p, member_type: v }))}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_member">Team Member (full access + mobile login)</SelectItem>
                  <SelectItem value="sub_contractor">Sub Contractor (calendar only, no login)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Name *</Label>
              <Input value={newMember.name} onChange={(e) => setNewMember(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="rounded-sm mt-1" data-testid="member-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact Number</Label>
              <Input value={newMember.contact_number} onChange={(e) => setNewMember(p => ({ ...p, contact_number: e.target.value }))} placeholder="07xxx xxx xxx" className="rounded-sm mt-1" data-testid="member-phone" />
            </div>
            {newMember.member_type === "sub_contractor" && (
              <p className="text-xs p-2.5 rounded-lg" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>
                Sub contractors appear on the calendar for booking but do not get a mobile login or appear in contractor pay.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
            <Button onClick={handleAddMember} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="confirm-add-member">
              <Plus className="w-4 h-4" /> Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Preview Dialog */}
      {previewJob && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={!!previewJob} onOpenChange={() => setPreviewJob(null)}>
        <DialogContent data-testid="job-preview-dialog" style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", maxHeight: "90vh", overflowY: "auto" }}>
          {previewJob && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {previewJob.booking_type === "quote" ? "Quote Appointment" : previewJob.booking_type === "recall" ? "Recall" : "Contractor Job Sheet"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-1" style={{ maxWidth: "100%" }}>
                {/* Job info */}
                <div className="p-3 rounded-lg" style={{ background: "var(--bg)" }}>
                  <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{previewJob.site_address || "—"}</p>
                  <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{previewJob.client_type}</p>
                  {previewJob.job_details && <p className="text-xs mt-2 whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{previewJob.job_details}</p>}
                </div>

                {/* Team Member */}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Team Member</Label>
                  <Select value={editMemberId} onValueChange={setEditMemberId}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {teamMembers.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: m.color }} />
                            {m.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date + Time — quotes get start+end dates just like execution */}
                {previewJob?.booking_type === "quote" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Start Date</Label>
                        <Popover open={editDateOpen} onOpenChange={setEditDateOpen}>
                          <PopoverTrigger asChild>
                            <button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                              {editDate || "Select"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                            <Calendar mode="single" selected={editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) { setEditDate(format(d, "dd/MM/yyyy")); if (!editEndDate || editEndDate === editDate) setEditEndDate(format(d, "dd/MM/yyyy")); } setEditDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>End Date</Label>
                        <Popover open={editEndDateOpen} onOpenChange={setEditEndDateOpen}>
                          <PopoverTrigger asChild>
                            <button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                              {editEndDate || editDate || "Same day"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                            <Calendar mode="single" selected={editEndDate ? parse(editEndDate, "dd/MM/yyyy", new Date()) : (editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : undefined)} onSelect={(d) => { if (d) { const sd = editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : null; if (sd && d < sd) { setEditDate(format(d, "dd/MM/yyyy")); setEditEndDate(format(sd, "dd/MM/yyyy")); } else { setEditEndDate(format(d, "dd/MM/yyyy")); } } setEditEndDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs" style={{ color: "var(--text-muted)" }}>Start Time</Label>
                        <Select value={editStartTime} onValueChange={(v) => { setEditStartTime(v); const [h,m] = v.split(":").map(Number); setEditEndTime(`${String(Math.floor((h*60+m+30)/60)).padStart(2,"0")}:${String((h*60+m+30)%60).padStart(2,"0")}`); }}>
                          <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs" style={{ color: "var(--text-muted)" }}>End Time</Label>
                        <Select value={editEndTime} onValueChange={setEditEndTime}>
                          <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIME_SLOTS.filter(t => editEndDate && editDate && editEndDate !== editDate ? true : t > editStartTime).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Start Date</Label>
                        <Popover open={editDateOpen} onOpenChange={setEditDateOpen}>
                          <PopoverTrigger asChild>
                            <button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                              {editDate || "Select"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                            <Calendar mode="single" selected={editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) { setEditDate(format(d, "dd/MM/yyyy")); const ed = editEndDate ? parse(editEndDate, "dd/MM/yyyy", new Date()) : null; if (ed && d > ed) setEditEndDate(format(d, "dd/MM/yyyy")); } setEditDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>End Date</Label>
                        <Popover open={editEndDateOpen} onOpenChange={setEditEndDateOpen}>
                          <PopoverTrigger asChild>
                            <button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                              {editEndDate || editDate || "Select"}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                            <Calendar mode="single" selected={editEndDate ? parse(editEndDate, "dd/MM/yyyy", new Date()) : (editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : undefined)} onSelect={(d) => { if (d) { const sd = editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : null; if (sd && d < sd) { setEditDate(format(d, "dd/MM/yyyy")); setEditEndDate(format(sd, "dd/MM/yyyy")); } else { setEditEndDate(format(d, "dd/MM/yyyy")); } } setEditEndDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    {(() => {
                      const sd = editDate ? parse(editDate, "dd/MM/yyyy", new Date()) : null;
                      const ed = editEndDate ? parse(editEndDate, "dd/MM/yyyy", new Date()) : sd;
                      if (!sd || !ed) return null;
                      const totalDays = Math.round((ed - sd) / 86400000) + 1;
                      const workDays = countWorkingDays(sd, ed, editSkipWeekends);
                      const isMulti = totalDays > 1;
                      return (
                        <div className="space-y-2">
                          <p className="text-xs font-medium" style={{ color: "var(--primary)" }}>Duration: {workDays} working day{workDays !== 1 ? "s" : ""}{workDays !== totalDays ? ` (${totalDays} total)` : ""}</p>
                          {isMulti && (
                            <p className="text-[10px] p-2 rounded-lg" style={{ background: "var(--primary-light)", color: "var(--primary)" }}>
                              Multi-day: first day {editStartTime}–{WORKDAY_END}, middle days {WORKDAY_START}–{WORKDAY_END}, last day {WORKDAY_START}–{editEndTime}
                            </p>
                          )}
                          {totalDays >= 3 && (
                            <div className="flex items-center gap-3">
                              <Label className="text-xs text-slate-400">Skip:</Label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={editSkipWeekends === "saturday" || editSkipWeekends === "both"} onChange={(e) => { const hasSun = editSkipWeekends === "sunday" || editSkipWeekends === "both"; if (e.target.checked) setEditSkipWeekends(hasSun ? "both" : "saturday"); else setEditSkipWeekends(hasSun ? "sunday" : "none"); }} className="w-3.5 h-3.5 rounded accent-blue-600" /> Saturday</label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={editSkipWeekends === "sunday" || editSkipWeekends === "both"} onChange={(e) => { const hasSat = editSkipWeekends === "saturday" || editSkipWeekends === "both"; if (e.target.checked) setEditSkipWeekends(hasSat ? "both" : "sunday"); else setEditSkipWeekends(hasSat ? "saturday" : "none"); }} className="w-3.5 h-3.5 rounded accent-blue-600" /> Sunday</label>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs" style={{ color: "var(--text-muted)" }}>{editEndDate && editDate && editEndDate !== editDate ? "Start Time (first day)" : "Start Time"}</Label>
                        <Select value={editStartTime} onValueChange={setEditStartTime}>
                          <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs" style={{ color: "var(--text-muted)" }}>{editEndDate && editDate && editEndDate !== editDate ? "End Time (last day)" : "End Time"}</Label>
                        <Select value={editEndTime} onValueChange={setEditEndTime}>
                          <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{TIME_SLOTS.filter(t => editEndDate && editDate && editEndDate !== editDate ? true : t > editStartTime).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Additional Team Members */}
              <div className="pt-3 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Additional Team Members</Label>
                  {editAdditionalMembers.length < teamMembers.length - 1 && (
                    <button onClick={() => { const used = new Set([editMemberId, ...editAdditionalMembers.map(a=>a.team_member_id)]); const av = teamMembers.filter(m=>!used.has(m.id)); if (!av.length) return; setEditAdditionalMembers(p=>[...p,{team_member_id:av[0].id,start_date:"",end_date:"",start_time:editStartTime,end_time:editEndTime}]); }} className="text-xs font-semibold flex items-center gap-1" style={{ color: "var(--primary)" }}><Plus className="w-3 h-3" /> Add</button>
                  )}
                </div>
                {editAdditionalMembers.length === 0 ? <p className="text-xs" style={{ color: "var(--text-muted)" }}>Lead only</p> : (
                  <div className="space-y-3">{editAdditionalMembers.map((am,idx) => {
                    const used = new Set([editMemberId,...editAdditionalMembers.filter((_,i)=>i!==idx).map(a=>a.team_member_id)]);
                    const isMultiDay = editEndDate && editDate && editEndDate !== editDate;
                    return <div key={am.team_member_id || `am-${idx}`} className="p-2.5 rounded-lg space-y-2" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                      <div className="flex items-center gap-2">
                        <Select value={am.team_member_id} onValueChange={v=>setEditAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,team_member_id:v}:a))}><SelectTrigger className="h-8 rounded-lg text-xs flex-1"><SelectValue /></SelectTrigger><SelectContent>{teamMembers.filter(m=>!used.has(m.id)||m.id===am.team_member_id).map(m=><SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>
                        <button onClick={()=>setEditAdditionalMembers(p=>p.filter((_,i)=>i!==idx))} className="p-1 rounded hover:bg-red-50" style={{ color: "var(--text-muted)" }}><X className="w-3 h-3" /></button>
                      </div>
                      {isMultiDay && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-slate-400">Start Date</Label>
                            <Popover><PopoverTrigger asChild><button className="flex items-center gap-1 w-full h-7 px-2 border rounded-sm text-[11px] text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3 h-3 text-slate-400" />{am.start_date || editDate}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={am.start_date ? parse(am.start_date, "dd/MM/yyyy", new Date()) : parse(editDate, "dd/MM/yyyy", new Date())} onSelect={(d) => { if (d) setEditAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,start_date:format(d,"dd/MM/yyyy")}:a)); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover>
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-400">End Date</Label>
                            <Popover><PopoverTrigger asChild><button className="flex items-center gap-1 w-full h-7 px-2 border rounded-sm text-[11px] text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3 h-3 text-slate-400" />{am.end_date || editEndDate || editDate}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={am.end_date ? parse(am.end_date, "dd/MM/yyyy", new Date()) : parse(editEndDate || editDate, "dd/MM/yyyy", new Date())} onSelect={(d) => { if (d) setEditAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,end_date:format(d,"dd/MM/yyyy")}:a)); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-[10px] text-slate-400">Start Time</Label><Select value={am.start_time || editStartTime} onValueChange={v=>setEditAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,start_time:v}:a))}><SelectTrigger className="h-7 rounded-sm text-[11px]"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                        <div><Label className="text-[10px] text-slate-400">End Time</Label><Select value={am.end_time || editEndTime} onValueChange={v=>setEditAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,end_time:v}:a))}><SelectTrigger className="h-7 rounded-sm text-[11px]"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                      </div>
                    </div>;
                  })}</div>
                )}
              </div>

              {/* Access */}
              <div className="pt-3 mt-2" style={{ borderTop: "1px solid var(--border)" }}>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">Access</Label>
                <Select value={editAccessMethod || "none"} onValueChange={(v) => { setEditAccessMethod(v === "none" ? "" : v); setEditAccessDetails(""); }}>
                  <SelectTrigger className="h-9 rounded-lg text-xs"><SelectValue placeholder="Select access method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="keys_branch">Keys: Collect from Branch</SelectItem>
                    <SelectItem value="occupier">Access via Occupier</SelectItem>
                    <SelectItem value="other">Other / See Notes</SelectItem>
                  </SelectContent>
                </Select>
                {editAccessMethod === "keys_branch" && (
                  <div className="mt-2">
                    <Input value={editAccessDetails} onChange={(e) => setEditAccessDetails(e.target.value)} placeholder="e.g. Bairstow Eves, 123 High Street, London, E1 4AB" className="h-9 rounded-lg text-xs" style={{ border: "1px solid var(--border)" }} />
                    <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>Collect keys before attending the property.</p>
                  </div>
                )}
                {editAccessMethod === "occupier" && (
                  <div className="mt-2">
                    <Input value={editAccessDetails || previewJob?.tenant_phone || ""} onChange={(e) => setEditAccessDetails(e.target.value)} placeholder="Enter occupier contact number" className="h-9 rounded-lg text-xs" style={{ border: "1px solid var(--border)" }} />
                    {!editAccessDetails && previewJob?.tenant_phone ? (
                      <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Pulled from job record</p>
                    ) : editAccessDetails && editAccessDetails !== previewJob?.tenant_phone ? (
                      <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Edited</p>
                    ) : null}
                    <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>Tenant or occupier will provide access on arrival.</p>
                  </div>
                )}
                {editAccessMethod === "other" && (
                  <div className="mt-2">
                    <textarea value={editAccessDetails} onChange={(e) => setEditAccessDetails(e.target.value)} placeholder="Describe how to access the property..." className="w-full text-xs rounded-lg p-2 resize-y" style={{ border: "1px solid var(--border)", minHeight: "60px" }} />
                  </div>
                )}
              </div>

              <DialogFooter className="flex gap-2 flex-wrap">
                <Button variant="destructive" size="sm" onClick={handleDeleteBooking}>Delete</Button>
                <Button variant="outline" size="sm" onClick={() => {
                  // Add another booking to this job — enter booking mode for same job
                  const job = previewJob;
                  setPreviewJob(null);
                  setBookingQuote(job);
                  setCalView("week");
                  setBookStartTime("08:00");
                  setBookEndTime("17:00");
                  setAdditionalMembers([]);
                  setBookSkipWeekends("none");
                }} style={{ borderColor: "var(--primary)", color: "var(--primary)" }}>Add Booking</Button>
                <Button variant="outline" size="sm" onClick={() => { setPreviewJob(null); navigate(`/quotes/${previewJob.id}`); }}>View Job Pack</Button>
                <Button variant="outline" size="sm" onClick={handleReschedule}>Save Changes</Button>
                {(previewJob.booking_type === "job" || previewJob.booking_type === "recall" || previewJob.status === "Job Booked") && (
                  <Button size="sm" onClick={() => { setPreviewJob(null); navigate(`/jobs/${previewJob.id}`); }} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="open-jobsheet-btn">
                    Open Job Sheet
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Absence Dialog */}
      <Dialog open={showAbsence} onOpenChange={setShowAbsence}>
        <DialogContent data-testid="add-absence-dialog">
          <DialogHeader>
            <DialogTitle>Absence create</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500">Team member</Label>
              <Select value={absence.team_member_id} onValueChange={(v) => setAbsence(p => ({ ...p, team_member_id: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="absence-member">
                  <SelectValue placeholder="Choose team member" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: m.color }} />
                        {m.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Reason for absence</Label>
              <Select value={absence.reason} onValueChange={(v) => setAbsence(p => ({ ...p, reason: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="absence-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Overtime compensation", "Public holiday", "School", "Sick leave", "Sick leave (child)", "Vacation (paid)", "Vacation (unpaid)", "Other absence"].map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500">From</Label>
                <Popover open={absFromOpen} onOpenChange={setAbsFromOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-2 w-full h-9 mt-1 px-3 border border-slate-200 rounded-sm text-sm text-left hover:bg-slate-50" data-testid="absence-from">
                      <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                      <span className={absence.from_date ? "text-slate-700" : "text-slate-400"}>{absence.from_date || "dd/mm/yyyy"}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" sideOffset={4} style={{ zIndex: 9999 }}>
                    <Calendar mode="single" selected={absence.from_date ? parse(absence.from_date, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) { setAbsence(p => ({ ...p, from_date: format(d, "dd/MM/yyyy") })); if (!absence.to_date) setAbsence(p => ({ ...p, to_date: format(d, "dd/MM/yyyy") })); } setAbsFromOpen(false); }} weekStartsOn={1} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500">To</Label>
                <Popover open={absToOpen} onOpenChange={setAbsToOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-2 w-full h-9 mt-1 px-3 border border-slate-200 rounded-sm text-sm text-left hover:bg-slate-50" data-testid="absence-to">
                      <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                      <span className={absence.to_date ? "text-slate-700" : "text-slate-400"}>{absence.to_date || "dd/mm/yyyy"}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" sideOffset={4} style={{ zIndex: 9999 }}>
                    <Calendar mode="single" selected={absence.to_date ? parse(absence.to_date, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) setAbsence(p => ({ ...p, to_date: format(d, "dd/MM/yyyy") })); setAbsToOpen(false); }} weekStartsOn={1} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">Duration</Label>
              <Select value={absence.duration} onValueChange={(v) => setAbsence(p => ({ ...p, duration: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="absence-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full day">Full day</SelectItem>
                  <SelectItem value="First half of the day">First half of the day</SelectItem>
                  <SelectItem value="Second half of the day">Second half of the day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAbsence(false)}>Cancel</Button>
            <Button onClick={handleCreateAbsence} className="bg-sky-500 hover:bg-sky-600 text-white gap-1" data-testid="confirm-absence-btn">
              <Plus className="w-4 h-4" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absence Preview Dialog */}
      {previewAbsence && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={!!previewAbsence} onOpenChange={() => setPreviewAbsence(null)}>
        <DialogContent data-testid="absence-preview-dialog" style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          {previewAbsence && (
            <>
              <DialogHeader>
                <DialogTitle>Absence</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 py-2 text-sm">
                <div><span className="text-xs text-slate-400 uppercase">Team Member</span><p className="font-medium">{previewAbsence.team_member_name}</p></div>
                <div><span className="text-xs text-slate-400 uppercase">Reason</span><p className="font-medium">{previewAbsence.reason}</p></div>
                <div className="flex gap-4">
                  <div><span className="text-xs text-slate-400 uppercase">From</span><p className="font-medium">{previewAbsence.from_date}</p></div>
                  <div><span className="text-xs text-slate-400 uppercase">To</span><p className="font-medium">{previewAbsence.to_date}</p></div>
                </div>
                <div><span className="text-xs text-slate-400 uppercase">Duration</span><p className="font-medium">{previewAbsence.duration}</p></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPreviewAbsence(null)}>Close</Button>
                <Button variant="destructive" onClick={async () => {
                  try { await deleteAbsence(previewAbsence.id); toast.success("Absence deleted"); setPreviewAbsence(null); fetchData(); } catch { toast.error("Failed to delete"); }
                }} data-testid="delete-absence-btn">Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Appointment Dialog */}
      {showAppt && <div data-overlay="appt" style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={showAppt} onOpenChange={setShowAppt}>
        <DialogContent style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", maxHeight: "85vh", overflowY: "auto" }}>
          <DialogHeader><DialogTitle>Add Appointment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase text-slate-500">Team Member *</Label>
              <Select value={newAppt.team_member_id} onValueChange={(v) => setNewAppt(p => ({ ...p, team_member_id: v }))}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Title *</Label>
              <Input value={newAppt.title} onChange={(e) => setNewAppt(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Site survey, Client meeting" className="rounded-sm mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-slate-500">Start Date *</Label>
                <Popover open={apptDateOpen} onOpenChange={setApptDateOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                      <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                      {newAppt.date || "Select"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                    <Calendar mode="single" selected={newAppt.date ? parse(newAppt.date, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) { setNewAppt(p => ({ ...p, date: format(d, "dd/MM/yyyy"), end_date: p.end_date || format(d, "dd/MM/yyyy") })); } setApptDateOpen(false); }} weekStartsOn={1} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                      <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                      {newAppt.end_date || newAppt.date || "Same day"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                    <Calendar mode="single" selected={newAppt.end_date ? parse(newAppt.end_date, "dd/MM/yyyy", new Date()) : (newAppt.date ? parse(newAppt.date, "dd/MM/yyyy", new Date()) : undefined)} onSelect={(d) => { if (d) setNewAppt(p => ({ ...p, end_date: format(d, "dd/MM/yyyy") })); }} weekStartsOn={1} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Start Time</Label>
                <Select value={newAppt.start_time} onValueChange={(v) => setNewAppt(p => ({ ...p, start_time: v }))}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">End Time</Label>
                <Select value={newAppt.end_time} onValueChange={(v) => setNewAppt(p => ({ ...p, end_time: v }))}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.filter(t => t > newAppt.start_time).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Notes</Label>
              <textarea value={newAppt.notes} onChange={(e) => setNewAppt(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes..." className="w-full text-sm rounded-sm p-2 mt-1" style={{ border: "1px solid var(--border)", minHeight: "100px", resize: "vertical" }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppt(false)}>Cancel</Button>
            <Button onClick={handleCreateAppt} className="btn-primary">Create Appointment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Copy Dialog */}
      <Dialog open={!!showCopyJob} onOpenChange={() => setShowCopyJob(null)}>
        <DialogContent style={{ maxHeight: "85vh", overflowY: "auto" }}>
          {showCopyJob && (
            <>
              <DialogHeader><DialogTitle>Create Copy of Booking</DialogTitle></DialogHeader>
              <div className="p-3 rounded-lg mb-3" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                <p className="text-sm font-semibold">{showCopyJob.site_address}</p>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{showCopyJob.quote_number} · {showCopyJob.client_type}</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">New Date *</Label>
                  <Popover open={copyDateOpen} onOpenChange={setCopyDateOpen}>
                    <PopoverTrigger asChild>
                      <button className="flex items-center gap-2 w-full h-10 mt-1 px-3 border rounded-lg text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                        <CalendarDays className="w-4 h-4 text-slate-400" /> {copyDate || "Select date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
                      <Calendar mode="single" selected={copyDate ? parse(copyDate, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) setCopyDate(format(d, "dd/MM/yyyy")); setCopyDateOpen(false); }} weekStartsOn={1} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <DialogFooter className="gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowCopyJob(null)}>Cancel</Button>
                <Button onClick={async () => {
                  if (!copyDate) { toast.error("Select a date"); return; }
                  try {
                    await bookJob(showCopyJob.id, {
                      start_date: copyDate,
                      end_date: copyDate,
                      team_member_id: showCopyJob.booked_team_member_id,
                      booked_start_time: showCopyJob.booked_start_time || "08:00",
                      booked_end_time: showCopyJob.booked_end_time || "17:00",
                      additional_members: showCopyJob.additional_team_members || [],
                    });
                    toast.success("Booking copied to " + copyDate);
                    setShowCopyJob(null);
                    fetchData();
                  } catch (e) { toast.error("Failed to copy booking"); }
                }} className="btn-primary">Create Copy</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Email Prompt */}
      <Dialog open={!!showBookingEmailPrompt} onOpenChange={() => setShowBookingEmailPrompt(null)}>
        <DialogContent>
          {showBookingEmailPrompt && (() => {
            const { quote: bq, date, time, isReschedule } = showBookingEmailPrompt;
            const clientName = [bq.client_first_name, bq.client_last_name].filter(Boolean).join(" ") || bq.client_type || "";
            const defaultSubject = isReschedule
              ? `Updated Booking - ${bq.site_address || ""}`
              : `Booking Confirmation - ${bq.site_address || ""}`;
            const defaultMessage = isReschedule
              ? `Hi ${clientName},\n\nThis is to let you know that your appointment at ${bq.site_address || "the property"} has been rescheduled.\n\nYour new appointment is on ${date} at ${time}.\n\nIf you have any questions or need to reschedule, please give us a call on 020 3633 8748.\n\nMany Thanks\nHPS Team`
              : `Hi ${clientName},\n\nThis is to confirm your appointment at ${bq.site_address || "the property"} has been booked in.\n\nYour appointment is on ${date} at ${time}.\n\nIf you have any questions or need to reschedule, please give us a call on 020 3633 8748.\n\nMany Thanks\nHPS Team`;
            return (
              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                try {
                  await api.post("/send-booking-email", { to_email: fd.get("to_email"), cc_email: fd.get("cc_email"), subject: fd.get("subject"), message: fd.get("message"), job_id: bq.id });
                  toast.success("Email sent");
                } catch (e) { toast.error("Failed to send"); }
                setShowBookingEmailPrompt(null);
              }}>
                <DialogHeader><DialogTitle>{isReschedule ? "Send Reschedule Email?" : "Send Booking Confirmation Email?"}</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div><Label className="text-xs uppercase text-slate-500">To</Label><input name="to_email" defaultValue={bq.correspondence_email || ""} className="w-full h-9 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)" }} /></div>
                  <div><Label className="text-xs uppercase text-slate-500">CC</Label><input name="cc_email" defaultValue="" placeholder="Optional" className="w-full h-9 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)" }} /></div>
                  <div><Label className="text-xs uppercase text-slate-500">Subject</Label><input name="subject" defaultValue={defaultSubject} className="w-full h-9 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)" }} /></div>
                  <div><Label className="text-xs uppercase text-slate-500">Message</Label><textarea name="message" defaultValue={defaultMessage} rows={5} className="w-full rounded-lg p-3 text-sm mt-1 resize-y" style={{ border: "1px solid var(--border)" }} /></div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowBookingEmailPrompt(null)}>Skip</Button>
                  <Button type="submit" className="btn-primary gap-1"><Send className="w-4 h-4" /> Send Email</Button>
                </DialogFooter>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Preview/Edit Appointment Dialog */}
      {previewAppt && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={!!previewAppt} onOpenChange={() => setPreviewAppt(null)}>
        <DialogContent style={{ width: "700px", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", maxHeight: "85vh", overflowY: "auto" }}>
          {previewAppt && (
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              try {
                await api.put(`/appointments/${previewAppt.id}`, {
                  title: fd.get("title"),
                  team_member_id: fd.get("team_member_id"),
                  date: fd.get("start_date"),
                  end_date: fd.get("end_date") || fd.get("start_date"),
                  start_time: fd.get("start_time"),
                  end_time: fd.get("end_time"),
                  notes: fd.get("notes"),
                });
                toast.success("Appointment updated");
                setPreviewAppt(null);
                fetchData();
              } catch (e) { toast.error("Failed to update"); }
            }}>
              <DialogHeader><DialogTitle className="text-lg">Appointment</DialogTitle></DialogHeader>

              {/* Appointment info */}
              <div className="p-4 rounded-xl mb-4" style={{ background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.2)" }}>
                <input name="title" defaultValue={previewAppt.title} className="text-base font-semibold w-full bg-transparent focus:outline-none focus:bg-white focus:ring-1 rounded px-1 -ml-1" style={{ color: "var(--text-primary)" }} placeholder="Appointment title" />
                {previewAppt.notes !== undefined && (
                  <textarea name="notes" defaultValue={previewAppt.notes || ""} placeholder="Notes..." rows={2} className="w-full text-xs mt-2 bg-transparent focus:outline-none focus:bg-white focus:ring-1 rounded p-1 -ml-1 resize-y" style={{ color: "var(--text-secondary)" }} />
                )}
              </div>

              <div className="space-y-4">
                {/* Team member */}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Team Member</Label>
                  <Select name="team_member_id" defaultValue={previewAppt.team_member_id}>
                    <SelectTrigger className="h-10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{teamMembers.map(m => <SelectItem key={m.id} value={m.id}><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: m.color }} />{m.name}</span></SelectItem>)}</SelectContent>
                  </Select>
                </div>

                {/* Start Date + End Date */}
                <ApptDateFields startDate={previewAppt.date} endDate={previewAppt.end_date || previewAppt.date} />

                {/* Times */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Start Time</Label>
                    <Select name="start_time" defaultValue={previewAppt.start_time}>
                      <SelectTrigger className="h-10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">End Time</Label>
                    <Select name="end_time" defaultValue={previewAppt.end_time}>
                      <SelectTrigger className="h-10 rounded-lg mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex gap-2 mt-4 flex-wrap">
                <Button type="button" variant="destructive" size="sm" onClick={async () => { try { await deleteAppointment(previewAppt.id); toast.success("Deleted"); setPreviewAppt(null); fetchData(); } catch { toast.error("Failed"); } }}>Delete</Button>
                <Button type="submit" className="btn-primary">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ApptDateFields({ startDate, endDate }) {
  const [sd, setSd] = useState(startDate || "");
  const [ed, setEd] = useState(endDate || startDate || "");
  const [sdOpen, setSdOpen] = useState(false);
  const [edOpen, setEdOpen] = useState(false);
  const isMulti = sd && ed && sd !== ed;
  const duration = (() => {
    if (!sd || !ed) return null;
    try {
      const s = parse(sd, "dd/MM/yyyy", new Date());
      const e = parse(ed, "dd/MM/yyyy", new Date());
      const days = countWorkingDays(s, e, "none");
      return days > 1 ? `Duration: ${days} working days` : null;
    } catch { return null; }
  })();
  return (
    <>
      <input type="hidden" name="start_date" value={sd} />
      <input type="hidden" name="end_date" value={ed} />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Start Date</Label>
          <Popover open={sdOpen} onOpenChange={setSdOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 w-full h-10 mt-1 px-3 border rounded-lg text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                <CalendarDays className="w-3.5 h-3.5 text-slate-400" />{sd || "Select"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
              <Calendar mode="single" selected={sd ? parse(sd, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) { const f = format(d, "dd/MM/yyyy"); setSd(f); if (!ed || (ed && parse(ed, "dd/MM/yyyy", new Date()) < d)) setEd(f); } setSdOpen(false); }} weekStartsOn={1} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">End Date</Label>
          <Popover open={edOpen} onOpenChange={setEdOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 w-full h-10 mt-1 px-3 border rounded-lg text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                <CalendarDays className="w-3.5 h-3.5 text-slate-400" />{isMulti ? ed : "Same day"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}>
              <Calendar mode="single" selected={ed ? parse(ed, "dd/MM/yyyy", new Date()) : undefined} onSelect={(d) => { if (d) { const f = format(d, "dd/MM/yyyy"); if (sd && d < parse(sd, "dd/MM/yyyy", new Date())) { setEd(sd); setSd(f); } else { setEd(f); } } setEdOpen(false); }} weekStartsOn={1} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {duration && <p className="text-xs font-medium" style={{ color: "var(--primary)" }}>{duration}</p>}
    </>
  );
}



function MonthlyGrid({ monthDate, quotes, appointments, teamMembers, memberFilter, onEventClick, onApptClick, onDayClick }) {
  const [expandedDay, setExpandedDay] = useState(null);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const today = new Date(); today.setHours(0,0,0,0);

  // Build grid starting from Monday
  const startOffset = (firstDay.getDay() + 6) % 7; // 0=Mon
  const gridStart = addDays(firstDay, -startOffset);
  const totalCells = Math.ceil((startOffset + lastDay.getDate()) / 7) * 7;
  const days = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));

  // Map events by date key
  const eventsByDate = {};
  quotes.forEach(q => {
    if (!q.start_date || !q.booked_team_member_id) return;
    try {
      const sd = parse(q.start_date, "dd/MM/yyyy", new Date());
      const ed = q.end_date ? parse(q.end_date, "dd/MM/yyyy", new Date()) : sd;
      let d = sd;
      while (d <= ed) {
        const key = format(d, "yyyy-MM-dd");
        if (!eventsByDate[key]) eventsByDate[key] = [];
        if (!eventsByDate[key].find(e => e.id === q.id && e.type === "job")) {
          const addMembers = (q.additional_team_members || []).length;
          eventsByDate[key].push({ type: "job", data: q, id: q.id, label: q.booking_type === "quote" ? "Quote Appt" : q.booking_type === "recall" ? "Recall" : "Execution", color: q.booking_type === "quote" ? "#1D4ED8" : q.booking_type === "recall" ? "#EA580C" : "#0D9488", memberId: q.booked_team_member_id, startTime: q.booked_start_time || "08:00", endTime: q.booked_end_time || "17:00", extraMembers: addMembers });
        }
        d = addDays(d, 1);
      }
      (q.additional_bookings || []).forEach(ab => {
        try {
          const abs = parse(ab.start_date, "dd/MM/yyyy", new Date());
          const abe = ab.end_date ? parse(ab.end_date, "dd/MM/yyyy", new Date()) : abs;
          let dd = abs;
          while (dd <= abe) {
            const key = format(dd, "yyyy-MM-dd");
            if (!eventsByDate[key]) eventsByDate[key] = [];
            eventsByDate[key].push({ type: "job", data: q, id: q.id + "_" + ab.id, label: "Execution", color: "#0D9488", memberId: ab.team_member_id, startTime: ab.booked_start_time || "08:00", endTime: ab.booked_end_time || "17:00", extraMembers: 0 });
            dd = addDays(dd, 1);
          }
        }  catch (e) {}
      });
    } catch (e) {}
  });
  appointments.forEach(ap => {
    try {
      const sd = parse(ap.date || ap.start_date, "dd/MM/yyyy", new Date());
      const ed = ap.end_date ? parse(ap.end_date, "dd/MM/yyyy", new Date()) : sd;
      let d = sd;
      while (d <= ed) {
        const key = format(d, "yyyy-MM-dd");
        if (!eventsByDate[key]) eventsByDate[key] = [];
        eventsByDate[key].push({ type: "appt", data: ap, id: ap.id, label: "Appt", color: "#7C3AED", memberId: ap.team_member_id, startTime: ap.start_time || "08:00", endTime: ap.end_time || "17:00", extraMembers: 0 });
        d = addDays(d, 1);
      }
    }  catch (e) {}
  });

  const renderPill = (ev, ei) => {
    const member = teamMembers.find(m => m.id === ev.memberId);
    const memberName = member ? member.name.split(" ")[0] : "";
    return (
      <div
        key={ei}
        onClick={(e) => { e.stopPropagation(); ev.type === "job" ? onEventClick(ev.data) : onApptClick(ev.data); }}
        className="flex flex-col gap-0 rounded cursor-pointer hover:opacity-80 transition-opacity"
        style={{ background: `${ev.color}12`, border: `1px solid ${ev.color}25`, padding: "4px 6px", minHeight: "36px" }}
      >
        <div className="flex items-center gap-1 text-[10px] font-semibold truncate" style={{ color: ev.color }}>
          {member && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: member.color || ev.color }} />}
          <span className="truncate">{ev.label}: {ev.type === "job" ? (ev.data.site_address || "").split(",")[0] : ev.data.title}</span>
        </div>
        <div className="flex items-center gap-2 text-[9px]" style={{ color: "#6B7280" }}>
          <span>{ev.startTime} - {ev.endTime}</span>
          {memberName && <span className="truncate">{memberName}{ev.extraMembers > 0 ? ` +${ev.extraMembers}` : ""}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="px-8 py-3">
      <div className="ui-surface overflow-hidden p-4">
        {/* Day of week headers */}
        <div className="grid grid-cols-7 mb-1">
          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
            <div key={d} className="text-center text-[11px] font-bold uppercase py-2" style={{ color: "#6B7280" }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div className="grid grid-cols-7 border-t border-l" style={{ borderColor: "var(--border)" }}>
          {days.map((day, i) => {
            const key = format(day, "yyyy-MM-dd");
            const isToday = isSameDay(day, today);
            const isCurrentMonth = day.getMonth() === month;
            const allEvents = eventsByDate[key] || [];
            const events = memberFilter && memberFilter !== "all" ? allEvents.filter(e => e.memberId === memberFilter) : allEvents;
            const isExpanded = expandedDay === key;
            const maxShow = 2;
            return (
              <div
                key={i}
                onClick={() => events.length === 0 && onDayClick(day)}
                className="border-r border-b p-1.5 transition-all cursor-pointer hover:bg-slate-50"
                style={{ borderColor: isExpanded ? "#E05C2A" : "var(--border)", background: isExpanded ? "#FFF9F7" : isToday ? "#FFF5F2" : !isCurrentMonth ? "#FAFAFA" : "transparent", minHeight: isExpanded ? "auto" : "100px" }}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs" style={{ color: isToday ? "#E05C2A" : !isCurrentMonth ? "#9CA3AF" : "var(--text-primary)", fontWeight: isToday ? 700 : 500 }}>
                    {format(day, "d")}
                  </span>
                </div>
                <div className="space-y-1">
                  {(isExpanded ? events : events.slice(0, maxShow)).map((ev, ei) => renderPill(ev, ei))}
                  {!isExpanded && events.length > maxShow && (
                    <button onClick={(e) => { e.stopPropagation(); setExpandedDay(key); }} className="w-full text-center py-1 rounded text-[11px] font-medium" style={{ background: "#F3F4F6", color: "#6B7280" }}>
                      +{events.length - maxShow} more
                    </button>
                  )}
                  {isExpanded && events.length > maxShow && (
                    <button onClick={(e) => { e.stopPropagation(); setExpandedDay(null); }} className="w-full text-center py-1 rounded text-[11px] font-medium" style={{ background: "#F3F4F6", color: "#6B7280" }}>
                      Show less
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function DraggableBookPanel({ bookingQuote, bookStartDate, bookEndDate, bookStartTime, bookEndTime, bookMemberId, bookStartDateOpen, bookEndDateOpen, setBookStartDate, setBookEndDate, setBookStartTime, setBookEndTime, setBookMemberId, setBookStartDateOpen, setBookEndDateOpen, teamMembers, additionalMembers, setAdditionalMembers, overlapWarning, onConfirm, onCancel, isSameDay, skipWeekends, setSkipWeekends }) {
  const [pos, setPos] = useState({ x: window.innerWidth / 2 - 190, y: 80 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const onMouseDown = (e) => { if (e.target.closest("button, select, input, [role='combobox'], [data-radix-collection-item]")) return; setDragging(true); dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  useEffect(() => { if (!dragging) return; const onMove = (e) => setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y }); const onUp = () => setDragging(false); document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp); return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); }; }, [dragging]);
  if (!bookingQuote || !bookStartDate) return null;
  const isQuote = bookingQuote.status === "Not Started";
  return (
    <div className="fixed z-[200] bg-white rounded-xl shadow-2xl border" style={{ left: pos.x, top: pos.y, width: 520, maxHeight: "85vh", borderColor: "var(--border)", cursor: dragging ? "grabbing" : "default", overflowY: "auto" }} onMouseDown={onMouseDown} data-testid="book-job-dialog">
      <div className="px-4 py-3 flex items-center justify-between cursor-grab active:cursor-grabbing" style={{ borderBottom: "1px solid var(--border)", background: "var(--bg)" }}>
        <h3 className="text-sm font-semibold">{isQuote ? "Book Quote" : bookingQuote.status === "Recall" ? "Book Recall" : "Book Execution"}</h3>
        <button onClick={onCancel} className="p-1 rounded hover:bg-slate-200"><X className="w-4 h-4" style={{ color: "var(--text-muted)" }} /></button>
      </div>
      <div className="p-4 space-y-3">
        <div className="p-2.5 rounded-lg text-sm" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
          <div className="font-semibold" style={{ color: "var(--text-primary)" }}>{bookingQuote.site_address || "No address"}</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{bookingQuote.client_type}</div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Team Member</Label>
          <Select value={bookMemberId} onValueChange={setBookMemberId}><SelectTrigger className="rounded-sm mt-1" data-testid="book-member-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{teamMembers.map(m => <SelectItem key={m.id} value={m.id}><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: m.color }} />{m.name}</span></SelectItem>)}</SelectContent></Select>
        </div>
        {isQuote ? (
          <>
            <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Date</Label><Popover open={bookStartDateOpen} onOpenChange={setBookStartDateOpen}><PopoverTrigger asChild><button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3.5 h-3.5 text-slate-400" />{format(bookStartDate, "dd/MM/yyyy")}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={bookStartDate} onSelect={(d) => { if (d) { setBookStartDate(d); setBookEndDate(d); } setBookStartDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Start Time</Label><Select value={bookStartTime} onValueChange={(v) => { setBookStartTime(v); const [h,m] = v.split(":").map(Number); setBookEndTime(`${String(Math.floor((h*60+m+30)/60)).padStart(2,"0")}:${String((h*60+m+30)%60).padStart(2,"0")}`); }}><SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-400">Duration</Label><Select value={bookEndTime} onValueChange={setBookEndTime}><SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{[20,30,60].map(mins => { const [h,m]=bookStartTime.split(":").map(Number); const total=h*60+m+mins; const et=`${String(Math.floor(total/60)).padStart(2,"0")}:${String(total%60).padStart(2,"0")}`; return <SelectItem key={mins} value={et}>{mins===60?"1 hour":`${mins} mins`} (until {et})</SelectItem>; })}</SelectContent></Select></div>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Start Date</Label><Popover open={bookStartDateOpen} onOpenChange={setBookStartDateOpen}><PopoverTrigger asChild><button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3.5 h-3.5 text-slate-400" />{format(bookStartDate, "dd/MM/yyyy")}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={bookStartDate} onSelect={(d) => { if (d) { setBookStartDate(d); if (bookEndDate && d > bookEndDate) setBookEndDate(d); } setBookStartDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover></div>
              <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">End Date</Label><Popover open={bookEndDateOpen} onOpenChange={setBookEndDateOpen}><PopoverTrigger asChild><button className="flex items-center gap-1.5 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3.5 h-3.5 text-slate-400" />{bookEndDate ? format(bookEndDate, "dd/MM/yyyy") : format(bookStartDate, "dd/MM/yyyy")}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={bookEndDate || bookStartDate} onSelect={(d) => { if (d) { if (d < bookStartDate) { setBookStartDate(d); setBookEndDate(bookStartDate); } else setBookEndDate(d); } setBookEndDateOpen(false); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover></div>
            </div>
            {bookEndDate && bookStartDate && (() => { const totalDays = Math.round((bookEndDate - bookStartDate) / 86400000) + 1; const workDays = countWorkingDays(bookStartDate, bookEndDate, skipWeekends); return totalDays > 1 ? <p className="text-xs font-medium" style={{ color: "var(--primary)" }}>Duration: {workDays} working day{workDays !== 1 ? "s" : ""}{workDays !== totalDays ? ` (${totalDays} total)` : ""}</p> : null; })()}
            {/* Skip Weekends */}
            {bookEndDate && bookStartDate && Math.round((bookEndDate - bookStartDate) / 86400000) >= 2 && (
              <div className="flex items-center gap-3">
                <Label className="text-xs text-slate-400">Skip:</Label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={skipWeekends === "saturday" || skipWeekends === "both"} onChange={(e) => { const hasSun = skipWeekends === "sunday" || skipWeekends === "both"; if (e.target.checked) setSkipWeekends(hasSun ? "both" : "saturday"); else setSkipWeekends(hasSun ? "sunday" : "none"); }} className="w-3.5 h-3.5 rounded accent-blue-600" /> Saturday</label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={skipWeekends === "sunday" || skipWeekends === "both"} onChange={(e) => { const hasSat = skipWeekends === "saturday" || skipWeekends === "both"; if (e.target.checked) setSkipWeekends(hasSat ? "both" : "sunday"); else setSkipWeekends(hasSat ? "saturday" : "none"); }} className="w-3.5 h-3.5 rounded accent-blue-600" /> Sunday</label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Start Time</Label><Select value={bookStartTime} onValueChange={setBookStartTime}><SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs text-slate-400">End Time</Label><Select value={bookEndTime} onValueChange={setBookEndTime}><SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.filter(t => bookEndDate && bookStartDate && !isSameDay(bookStartDate, bookEndDate) ? true : t > bookStartTime).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </>
        )}
        <div className="pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Additional Team</Label>
            {additionalMembers.length < teamMembers.length - 1 && <button onClick={() => { const used = new Set([bookMemberId, ...additionalMembers.map(a=>a.team_member_id)]); const av = teamMembers.filter(m=>!used.has(m.id)); if (!av.length) return; setAdditionalMembers(p=>[...p,{team_member_id:av[0].id,start_date:"",end_date:"",start_time:bookStartTime,end_time:bookEndTime}]); }} className="text-xs font-semibold flex items-center gap-1" style={{ color: "var(--primary)" }}><Plus className="w-3 h-3" /> Add</button>}
          </div>
          {additionalMembers.length === 0 ? <p className="text-xs" style={{ color: "var(--text-muted)" }}>Lead contractor only.</p> : <div className="space-y-3">{additionalMembers.map((am,idx) => { const used = new Set([bookMemberId,...additionalMembers.filter((_,i)=>i!==idx).map(a=>a.team_member_id)]);
            const amSd = am.start_date ? parse(am.start_date, "dd/MM/yyyy", new Date()) : bookStartDate;
            const amEd = am.end_date ? parse(am.end_date, "dd/MM/yyyy", new Date()) : (bookEndDate || bookStartDate);
            const amDays = amSd && amEd ? countWorkingDays(amSd, amEd, skipWeekends) : 1;
            return <div key={am.team_member_id || `am-${idx}`} className="p-2.5 rounded-lg space-y-2" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-2">
              <Select value={am.team_member_id} onValueChange={v=>setAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,team_member_id:v}:a))}><SelectTrigger className="h-7 rounded-sm text-xs flex-1"><SelectValue /></SelectTrigger><SelectContent>{teamMembers.filter(m=>!used.has(m.id)||m.id===am.team_member_id).map(m=><SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>
              <button onClick={()=>setAdditionalMembers(p=>p.filter((_,i)=>i!==idx))} className="p-1 rounded hover:bg-red-50" style={{ color: "var(--text-muted)" }}><X className="w-3 h-3" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-400">Start Date</Label>
                <Popover><PopoverTrigger asChild><button className="flex items-center gap-1 w-full h-7 px-2 border rounded-sm text-[11px] text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3 h-3 text-slate-400" />{am.start_date || (bookStartDate ? format(bookStartDate, "dd/MM/yyyy") : "Select")}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={amSd} onSelect={(d) => { if (d) setAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,start_date:format(d,"dd/MM/yyyy")}:a)); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover>
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">End Date</Label>
                <Popover><PopoverTrigger asChild><button className="flex items-center gap-1 w-full h-7 px-2 border rounded-sm text-[11px] text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}><CalendarDays className="w-3 h-3 text-slate-400" />{am.end_date || (bookEndDate ? format(bookEndDate, "dd/MM/yyyy") : (bookStartDate ? format(bookStartDate, "dd/MM/yyyy") : "Select"))}</button></PopoverTrigger><PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999 }}><Calendar mode="single" selected={amEd} onSelect={(d) => { if (d) setAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,end_date:format(d,"dd/MM/yyyy")}:a)); }} weekStartsOn={1} initialFocus modifiers={{ today: [] }} /></PopoverContent></Popover>
              </div>
            </div>
            {amDays > 1 && <p className="text-[10px] font-medium" style={{ color: "var(--primary)" }}>Duration: {amDays} working day{amDays !== 1 ? "s" : ""}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px] text-slate-400">Start Time</Label><Select value={am.start_time || bookStartTime} onValueChange={v=>setAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,start_time:v}:a))}><SelectTrigger className="h-7 rounded-sm text-[11px]"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-[10px] text-slate-400">End Time</Label><Select value={am.end_time || bookEndTime} onValueChange={v=>setAdditionalMembers(p=>p.map((a,i)=>i===idx?{...a,end_time:v}:a))}><SelectTrigger className="h-7 rounded-sm text-[11px]"><SelectValue /></SelectTrigger><SelectContent>{TIME_SLOTS.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>; })}</div>}
          {bookMemberId && <p className="text-[10px] mt-2 font-medium" style={{ color: "var(--primary)" }}>{teamMembers.find(m=>m.id===bookMemberId)?.name} = Lead Contractor</p>}
        </div>
        {overlapWarning && <div className="p-2.5 rounded-lg text-sm font-medium" style={{ background: "var(--danger-light)", color: "#B91C1C" }}>{overlapWarning}</div>}
      </div>
      <div className="px-4 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} disabled={!!overlapWarning} className={`gap-1 ${overlapWarning ? "bg-slate-300 text-slate-500" : "bg-sky-500 hover:bg-sky-600 text-white"}`} data-testid="confirm-book-btn">Confirm Booking</Button>
      </div>
    </div>
  );
}

